import { useApolloClient } from "@apollo/client";
import { getDataOrNull, notNull } from "@msys/common";
import {
  DataGrid,
  DebouncedSearchInput,
  GridColDef,
  isImage,
  isImageOr3dModel,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  LoadingSpinner,
  MediaCardItem,
  MediaListItem,
  ModalOpenButton,
  processAttachment,
  TagChips,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import { useFormik } from "formik";
import React from "react";
import { useLatest } from "react-use";
import {
  OrganisationQuoteTemplatesSortBy,
  OrganisationQuoteTemplatesSorting,
  ProjectType,
  TemplateSearchFiltersInput,
  TemplateSearchSortingInput,
} from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ButtonCircleWrapper } from "../../../commons/button/ButtonCircleWrapper.js";
import { SwitchCollectionViewButton } from "../../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../../commons/filters/FilterButton.js";
import { useAutoSave } from "../../../commons/hooks/useAutoSave.js";
import {
  Columns,
  useCardsNumber,
} from "../../../commons/hooks/useCardsNumber.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../../commons/hooks/useCollectionView.js";
import { PaginationModel } from "../../../commons/pagination/usePaginationParams.js";
import { RESULTS_PER_PAGE_OPTIONS } from "../../../constants.js";
import { assertNever } from "../../../utils.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import { PropertyValueChips } from "../../properties/PropertyValueChips.js";
import { useDataGridStateStore } from "../../users/useDataGridStateStore.js";
import { useListViewCustomQuickFilters } from "../../users/useListViewCustomQuickFilters.js";
import {
  getNonTemplateFormValues,
  getTemplateFormValues,
  TemplateFiltersFormValues,
  templateFiltersValidationSchema,
} from "../filters/TemplateFilterFields.js";
import { TemplateFilters } from "../filters/TemplateFilters.js";
import {
  getLockedFilters,
  omitFixedFilters,
} from "../filters/omitFixedFilters.js";
import {
  computeTemplateRelevantProperties,
  quoteTemplateSortingToSearchQuotesSorting,
  searchQuotesSortingToQuoteTemplatesSorting,
} from "../helpers.js";
import { useTemplateDocumentsAllAvailableActiveFilterChips } from "../useTemplateDocumentsActiveFilterChips.js";
import { TemplateQuotePublishingStatus } from "./TemplateQuotePublishingStatus.js";
import {
  TemplatesDisplay_QuoteTemplate_GalleryFragment,
  TemplatesDisplay_QuoteTemplate_ListFragment,
  TemplatesDisplay_QuoteTemplate_TableFragment,
  TemplatesDisplay_SearchTemplatesQueryVariables,
  useTemplatesDisplay_SearchTemplatesQuery,
} from "./TemplatesDisplay.generated.js";
import { TemplateQuotesFilterWithTemplateTypeModal } from "./TemplatesQuoteFilterModal.js";
import { TemplatesQuoteOverviewModal } from "./TemplatesQuoteOverviewModal.js";
import {
  hasTemplateTypeOrPropsInfo,
  TemplateTypeAndPropsInfo,
} from "./TemplateTypeAndPropsInfo.js";
import { useTypeOfTemplates } from "./useTypeOfTemplates.js";

type TemplatesCollectionView = Extract<
  CollectionView,
  "table" | "list" | "gallery"
>;
const DEFAULT_ALLOWED_VIEWS: TemplatesCollectionView[] = [
  "gallery",
  "list",
  "table",
];
export const DEFAULT_SORTING: TemplateSearchSortingInput[] = [
  {
    fieldSorting: {
      field: "createdAt",
      direction: "desc",
    },
  },
];
export const DEFAULT_FILTERS: TemplateSearchFiltersInput = {};
export interface TemplateBase {
  id: string;
  title: string;
  resolvedAsReadModelVersionNumber?: number | null;
  typeOfProject?: Array<ProjectType> | null;
}

export interface TemplatesDisplayProps {
  allowedViews?: TemplatesCollectionView[];
  defaultCollectionViewDesktop?: TemplatesCollectionView;
  defaultCollectionViewMobile?: TemplatesCollectionView;
  collectionViewPersistenceKey?: string;
  fixedFilters?: TemplateSearchFiltersInput;
  ///
  paginationModel: PaginationModel;
  setPaginationModel: (newPaginationModel: PaginationModel) => void;
  filters: TemplateSearchFiltersInput;
  setFilters: React.Dispatch<React.SetStateAction<TemplateSearchFiltersInput>>;
  resetFilters: () => void;
  sorting: OrganisationQuoteTemplatesSorting[];
  setSorting: (value: OrganisationQuoteTemplatesSorting[]) => void;
  queryBaseVariables: Pick<
    TemplatesDisplay_SearchTemplatesQueryVariables,
    "offset" | "limit"
  >;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  /** Property sortings (e.g. a placeholder's sorting defaults). They can't be
   *  expressed as a grid column, so they are appended to the query after the
   *  column sorting. */
  seedPropertySortings?: TemplateSearchSortingInput[];
}

export const TemplatesDisplay = ({
  allowedViews = DEFAULT_ALLOWED_VIEWS,
  collectionViewPersistenceKey = "templatesDisplay",
  defaultCollectionViewDesktop = "table",
  defaultCollectionViewMobile = "list",
  onInspectedTemplateClick,
  onTemplateClick,
  selectedTemplate,
  setSelectedTemplate,
  fixedFilters = DEFAULT_FILTERS,
  filters,
  setFilters,
  resetFilters,
  sorting,
  setSorting,
  queryBaseVariables,
  searchTerm,
  setSearchTerm,
  paginationModel,
  setPaginationModel,
  seedPropertySortings,
}: TemplatesDisplayProps & {
  onInspectedTemplateClick?: (template: TemplateBase) => void;
  onTemplateClick?: (template: TemplateBase) => void;
  selectedTemplate?: TemplateBase | null;
  setSelectedTemplate?: (template: TemplateBase | null) => void;
}) => {
  const currentUser = useUserData().currentUser!;
  const { t } = useTranslate(["Templates", "Global"]);
  const { isMinTablet } = useScreenWidth();

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<TemplatesCollectionView>(
      collectionViewPersistenceKey,
      defaultCollectionViewDesktop,
      defaultCollectionViewMobile
    );

  // The "available" default must live in combinedFilters (not only in the
  // listing query) so the facet-count query counts the same index_type set as
  // the listing — otherwise badge counts include drafts/store templates the
  // list never shows (MEIS-12847).
  const combinedFilters = React.useMemo(
    (): TemplateSearchFiltersInput => ({
      sources: ["available"],
      ...filters,
      // Only set fixed values override: a `null` fixed key means "no
      // constraint" and must not wipe the user's own selection.
      ...getLockedFilters(fixedFilters),
    }),
    [fixedFilters, filters]
  );

  // The editable filters without the fixed keys a caller may have seeded
  // them with — the filter dialog starts its draft from these, so applying it
  // never writes a fixed value back as an editable chip (MEIS-10975).
  const editableFilters = React.useMemo(
    () => omitFixedFilters(filters, fixedFilters),
    [filters, fixedFilters]
  );

  const templateSearchVariables = React.useMemo(
    () => ({
      searchTerm: searchTerm,
      filters: getNonTemplateFormValues(combinedFilters),
    }),
    [combinedFilters, searchTerm]
  );

  const templateFilterFormikProps = useFormik<TemplateFiltersFormValues>({
    initialValues: getTemplateFormValues(combinedFilters),
    validationSchema: templateFiltersValidationSchema,
    validateOnMount: true,
    onSubmit: async values => {
      // Fixed values are only mirrored into the form so its (disabled)
      // controls and facet counts stay scoped — never write them back into
      // the editable filters, or they resurface as chips after "Clear all".
      // Strip them from the existing state as well: callers may seed the
      // editable filters with a fixed value (e.g. the placeholder's type).
      setFilters(state => ({
        ...omitFixedFilters(state, fixedFilters),
        ...omitFixedFilters(values, fixedFilters),
      }));
    },
  });

  // Reinitialize from the combined filters, not just the editable ones:
  // otherwise clearing the filters also wipes the fixed values from the form
  // (MEIS-10975).
  useAutoSave({
    enableReinitialize: true,
    initialValues: getTemplateFormValues(combinedFilters),
    formikProps: templateFilterFormikProps,
  });

  const client = useApolloClient();
  const query = useTemplatesDisplay_SearchTemplatesQuery({
    client,
    variables: {
      ...queryBaseVariables,
      filters: {
        ...combinedFilters,
        createdAfter: combinedFilters?.createdAfter?.format("YYYY-MM-DD"),
        createdBefore: combinedFilters?.createdBefore?.format("YYYY-MM-DD"),
      },
      searchTerm,
      sorting: [
        ...quoteTemplateSortingToSearchQuotesSorting(sorting),
        ...(seedPropertySortings ?? []),
      ],
      limit: paginationModel.pageSize,
      offset: paginationModel.pageSize * paginationModel.page,
    },
  });

  const result = getDataOrNull(query.data?.searchTemplates);
  const templates = result?.edges.map(edge => edge.node) ?? [];
  const totalCount = result?.totalCount ?? 0;

  const [inspectedTemplate, setInspectedTemplate] =
    React.useState<TemplateBase | null>(null);

  const { spacing, sizeMeasureRef, columns } = useCardsNumber();

  const quickFilters = isMinTablet ? (
    <TemplateFilters
      formikProps={templateFilterFormikProps}
      templateSearchVariables={templateSearchVariables}
      fixedFilters={fixedFilters}
    />
  ) : undefined;

  const activeFilterChips = useTemplateDocumentsAllAvailableActiveFilterChips({
    filters,
    setFilters,
    fixedFilters,
  });

  const listViewName = "TemplatesAllAvailable";
  const { customQuickFilterSelect, addCustomFilterButton } =
    useListViewCustomQuickFilters(listViewName, filters, setFilters, [
      {
        filterName: t("Created by me", { ns: "Templates" }),
        filterValue: { createdByUserIds: [currentUser.id] },
      },
    ]);

  return (
    <Stack
      spacing={2}
      direction="row"
      sx={{
        alignItems: "stretch",
        height: "100%",
        minHeight: 0,
        flex: 1,
      }}
    >
      <Stack
        direction={"column"}
        spacing={1}
        ref={sizeMeasureRef}
        sx={{
          height: "100%",
          flex: 1,
          minWidth: 0,
        }}
      >
        <ListHeader
          elementsOrder="separate-rows"
          SwitchViewButton={
            <SwitchCollectionViewButton<TemplatesCollectionView>
              allowedViews={allowedViews}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          }
          QuickFilter={
            quickFilters || customQuickFilterSelect ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  flexShrink: 1,
                  flexGrow: 0,
                }}
              >
                {quickFilters}
                {customQuickFilterSelect}
              </Stack>
            ) : undefined
          }
          FilterButton={
            <ModalOpenButton
              Modal={TemplateQuotesFilterWithTemplateTypeModal}
              modalProps={{
                resetFilters,
                sorting,
                setSorting: sorting => setSorting(sorting),
                filters: editableFilters,
                setFilters,
                initialTemplateFormValues: templateFilterFormikProps.values,
                templateSearchVariables,
                fixedFilters,
              }}
            >
              <FilterButton />
            </ModalOpenButton>
          }
          ActiveFilterChips={
            <ListHeaderActiveFilters
              activeFilterChips={activeFilterChips}
              collapsedContent={
                <Typography variant="body2">
                  {t("Active filters", { ns: "Global" })}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      marginLeft: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {t("{count} results", {
                      ns: "Global",
                      count: activeFilterChips.length,
                    })}
                  </Typography>
                </Typography>
              }
              clearAllButton={
                <ListHeaderActiveFiltersActionButton
                  label={t("Clear all", { ns: "Global" })}
                  onClick={resetFilters}
                />
              }
              saveFiltersButton={addCustomFilterButton}
            />
          }
          SearchField={
            <Stack
              direction={"row"}
              spacing={1}
              sx={{
                flex: 1,
              }}
            >
              <DebouncedSearchInput
                defaultValue={searchTerm}
                placeholder={t("Search", {
                  ns: "Global",
                })}
                onChangeSearchTerm={setSearchTerm}
                autoFocus
                style={{ flexGrow: 1 }}
              />
            </Stack>
          }
        />
        {activeView === "table" ? (
          <TemplatesTable
            isLoading={query.loading}
            totalCount={totalCount}
            templates={templates}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            setInspectedTemplate={setInspectedTemplate}
            onTemplateClick={onTemplateClick}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            propertyFilters={filters.properties}
            sorting={sorting}
            setSorting={setSorting}
          />
        ) : activeView === "list" ? (
          <TemplatesList
            isLoading={query.loading}
            totalCount={totalCount}
            templates={templates}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            setInspectedTemplate={setInspectedTemplate}
            onTemplateClick={onTemplateClick}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            propertyFilters={filters.properties}
          />
        ) : activeView === "gallery" ? (
          <TemplatesGallery
            isLoading={query.loading}
            totalCount={totalCount}
            templates={templates}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            setInspectedTemplate={setInspectedTemplate}
            onTemplateClick={onTemplateClick}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            propertyFilters={filters.properties}
            spacing={spacing}
            columns={columns}
          />
        ) : (
          assertNever(activeView)
        )}
      </Stack>
      {inspectedTemplate && (
        <TemplatesQuoteOverviewModal
          docId={inspectedTemplate.id}
          versionNumber={inspectedTemplate.resolvedAsReadModelVersionNumber}
          handleClose={() => {
            setInspectedTemplate(null);
          }}
          dialogActionButton={
            onInspectedTemplateClick ? (
              <Button
                onClick={e => {
                  e.stopPropagation();
                  setInspectedTemplate(null);
                  onInspectedTemplateClick(inspectedTemplate);
                }}
                color="primary"
                variant="contained"
              >
                {t("Use this template", { ns: "Templates" })}
              </Button>
            ) : setSelectedTemplate ? (
              <Button
                onClick={e => {
                  e.stopPropagation();
                  setInspectedTemplate(null);
                  setSelectedTemplate(inspectedTemplate);
                }}
                color="primary"
                variant="contained"
              >
                {t("Select", { ns: "Global" })}
              </Button>
            ) : undefined
          }
          notInStack
        />
      )}
    </Stack>
  );
};

const TemplatesTable = ({
  isLoading,
  templates,
  totalCount,
  setInspectedTemplate,
  onTemplateClick,
  selectedTemplate,
  setSelectedTemplate,
  paginationModel,
  setPaginationModel,
  propertyFilters,
  sorting,
  setSorting,
}: {
  isLoading: boolean;
  templates: TemplatesDisplay_QuoteTemplate_TableFragment[];
  totalCount: number;
  setInspectedTemplate: (template: TemplateBase) => void;
  onTemplateClick?: (template: TemplateBase) => void;
  selectedTemplate?: TemplateBase | null;
  setSelectedTemplate?: (template: TemplateBase | null) => void;
  paginationModel: PaginationModel;
  setPaginationModel: (newPaginationModel: PaginationModel) => void;
  propertyFilters: TemplateSearchFiltersInput["properties"];
  sorting: OrganisationQuoteTemplatesSorting[];
  setSorting: (sorting: OrganisationQuoteTemplatesSorting[]) => void;
}) => {
  const { t } = useTranslate(["Templates", "Global"]);
  const { getFormattedDate, getFormattedPrice } = useFormatting();

  const { typeOfTemplatesLabels } = useTypeOfTemplates();
  const { projectTypeLabels } = useProjectTypes();

  const setInspectedTemplateRef = useLatest(setInspectedTemplate);

  const columns = React.useMemo(
    (): GridColDef<TemplatesDisplay_QuoteTemplate_TableFragment>[] => [
      {
        field: "title",
        headerName: t("Title", { ns: "Templates" }),
        flex: 2,
        minWidth: 100,
        sortable: true,
        renderCell: ({ row: template }) => template.title,
      },
      {
        field: "templateType",
        headerName: t("Template type", { ns: "Templates" }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.implementsTemplateType?.templateType?.title ?? "",
      },
      {
        field: "templateTypeProperties",
        headerName: t("Template type properties", { ns: "Templates" }),
        flex: 3,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row: template }) => {
          const props = computeTemplateRelevantProperties({
            rootItemProps: template.rootItem.props2,
            templateTypeProps:
              template.implementsTemplateType?.templateType?.props2 ?? [],
          });
          return (
            <PropertyValueChips
              highlightedKeys={propertyFilters?.map(p => p.key)}
              props={props}
              wrap={false}
            />
          );
        },
      },
      {
        field: "category",
        headerName: t("Template category", {
          ns: "Templates",
        }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) => template.category?.name ?? "",
      },
      {
        field: "typeOfTemplate",
        headerName: t("Type of template", { ns: "Templates" }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.typeOfTemplate
            ? typeOfTemplatesLabels[template.typeOfTemplate]
            : "",
      },
      {
        field: "typeOfProject",
        headerName: t("Type of project", { ns: "Templates" }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.typeOfProject && template.typeOfProject.length > 0
            ? template.typeOfProject
                .map(top => projectTypeLabels[top])
                .join(", ")
            : "",
      },
      {
        field: "tags",
        headerName: t("Tags", { ns: "Templates" }),
        flex: 1.5,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: template }) => (
          <TagChips tags={template.customTags} wrap={false} />
        ),
      },
      {
        field: "publishingStatus",
        headerName: t("Publishing", { ns: "Templates" }),
        flex: 0.75,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: template }) => (
          <TemplateQuotePublishingStatus
            doc={template}
            variant="body2"
            IconProps={{ fontSize: "small" }}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: t("Created", { ns: "Templates" }),
        width: 120,
        sortable: true,
        renderCell: ({ row: template }) => getFormattedDate(template.createdAt),
      },
      {
        field: "discountedPrice",
        headerName: t("Price", { ns: "Templates" }),
        width: 120,
        headerAlign: "right",
        align: "right",
        sortable: true,
        renderCell: ({ row: template }) =>
          getFormattedPrice(template.proposedCalculation?.priceNetTotal || 0),
      },
      {
        field: "actions",
        headerName: "",
        width: 50,
        sortable: false,
        resizable: false,
        renderCell: ({ row: template }) => (
          <IconButton
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              setInspectedTemplateRef.current(template);
            }}
          >
            <InfoOutlinedIcon color="secondary" />
          </IconButton>
        ),
      },
    ],
    [
      getFormattedDate,
      getFormattedPrice,
      projectTypeLabels,
      propertyFilters,
      setInspectedTemplateRef,
      t,
      typeOfTemplatesLabels,
    ]
  );

  const stateStore = useDataGridStateStore(`TemplatesDisplay`);

  const rowSelectionModel = React.useMemo(
    () =>
      ({
        type: "include",
        ids: new Set(selectedTemplate ? [selectedTemplate.id] : []),
      }) satisfies GridRowSelectionModel,
    [selectedTemplate]
  );

  return (
    <DataGrid<TemplatesDisplay_QuoteTemplate_TableFragment>
      stateStore={stateStore}
      density="compact"
      loading={isLoading}
      hideFooter={totalCount === 0}
      localeText={{
        noRowsLabel: t("There are no items to display", {
          ns: "Global",
        }),
        paginationRowsPerPage: t("Rows per page", {
          ns: "Global",
        }),
      }}
      sortModel={sorting
        .map(s => ({
          field: s.column,
          sort: s.direction,
        }))
        .filter(notNull)}
      onSortModelChange={newModel => {
        setSorting(
          newModel.map(({ field, sort }) => ({
            column: field as OrganisationQuoteTemplatesSortBy,
            direction: sort ?? "asc",
          }))
        );
      }}
      columns={columns}
      rowSelectionModel={rowSelectionModel}
      rows={templates}
      onRowClick={({ row: template }) => {
        if (onTemplateClick) {
          onTemplateClick(template);
        } else {
          setSelectedTemplate?.(
            selectedTemplate?.id === template.id ? null : template
          );
        }
      }}
      paginationModel={paginationModel}
      onPaginationModelChange={newPaginationModel => {
        setPaginationModel(newPaginationModel);
      }}
      pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
      rowCount={totalCount}
      initialState={{
        columns: {
          columnVisibilityModel: {
            typeOfTemplate: false,
            typeOfProject: false,
          },
        },
      }}
    />
  );
};

const TemplatesList = ({
  isLoading,
  templates,
  totalCount,
  setInspectedTemplate,
  onTemplateClick,
  selectedTemplate,
  setSelectedTemplate,
  paginationModel,
  setPaginationModel,
  propertyFilters,
}: {
  isLoading: boolean;
  templates: Array<TemplatesDisplay_QuoteTemplate_ListFragment>;
  totalCount: number;
  setInspectedTemplate: (template: TemplateBase) => void;
  onTemplateClick?: (template: TemplateBase) => void;
  selectedTemplate?: TemplateBase | null;
  setSelectedTemplate?: (template: TemplateBase | null) => void;
  paginationModel: PaginationModel;
  setPaginationModel: (newPaginationModel: PaginationModel) => void;
  propertyFilters: TemplateSearchFiltersInput["properties"];
}) => {
  const { t } = useTranslate(["Global"]);
  const { getFormattedPrice } = useFormatting();

  if (isLoading) return <LoadingSpinner />;
  if (!templates.length)
    return (
      <Box
        sx={{
          padding: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {t("There seems to be nothing here", {
          ns: "Global",
        })}
      </Box>
    );

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        overflow: "auto",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Stack
        direction="column"
        spacing={1}
        sx={{
          minHeight: 0,
          overflow: "auto",
          flex: 1,
        }}
      >
        {templates.map((template, index) => {
          const attachments = template.attachments.map(processAttachment);

          const props = computeTemplateRelevantProperties({
            rootItemProps: template.rootItem.props2,
            templateTypeProps:
              template.implementsTemplateType?.templateType?.props2 ?? [],
          });

          const Info = hasTemplateTypeOrPropsInfo({
            templateTypeTitle:
              template.implementsTemplateType?.templateType.title,
            props,
          }) ? (
            <TemplateTypeAndPropsInfo
              templateTypeTitle={
                template.implementsTemplateType?.templateType.title
              }
              props={props}
            />
          ) : null;

          return (
            <MediaListItem
              key={template.id}
              selected={template.id === selectedTemplate?.id}
              onClick={() => {
                if (onTemplateClick) {
                  onTemplateClick(template);
                } else {
                  setSelectedTemplate?.(
                    selectedTemplate?.id === template.id ? null : template
                  );
                }
              }}
              title={template.title}
              description={template.description}
              price={getFormattedPrice(
                template.proposedCalculation?.priceNetTotal ?? 0
              )}
              imageUrl={attachments.find(isImage)?.url}
              images={attachments.filter(isImageOr3dModel)}
              ActionButton={
                <IconButton
                  onClick={event => {
                    event.stopPropagation();
                    setInspectedTemplate(template);
                  }}
                >
                  <InfoOutlinedIcon color="secondary" />
                </IconButton>
              }
              Info={Info}
              Tags={
                template.customTags.length > 0 ? (
                  <TagChips tags={template.customTags} wrap={false} />
                ) : null
              }
            />
          );
        })}
      </Stack>
      <TablePagination
        component="div"
        count={totalCount}
        page={paginationModel.page}
        onPageChange={(event, newPage) => {
          setPaginationModel({
            pageSize: paginationModel.pageSize,
            page: newPage,
          });
        }}
        rowsPerPage={paginationModel.pageSize}
        onRowsPerPageChange={event => {
          setPaginationModel({
            pageSize: parseInt(event.target.value, 10),
            page: 0,
          });
        }}
        labelRowsPerPage={t("Rows per page:", {
          ns: "Global",
        })}
      />
    </Stack>
  );
};

const TemplatesGallery = ({
  isLoading,
  templates,
  totalCount,
  setInspectedTemplate,
  onTemplateClick,
  selectedTemplate,
  setSelectedTemplate,
  paginationModel,
  setPaginationModel,
  propertyFilters,
  spacing,
  columns,
}: {
  isLoading: boolean;
  templates: Array<TemplatesDisplay_QuoteTemplate_GalleryFragment>;
  totalCount: number;
  setInspectedTemplate: (template: TemplateBase) => void;
  onTemplateClick?: (template: TemplateBase) => void;
  selectedTemplate?: TemplateBase | null;
  setSelectedTemplate?: (template: TemplateBase | null) => void;
  paginationModel: PaginationModel;
  setPaginationModel: (newPaginationModel: PaginationModel) => void;
  propertyFilters: TemplateSearchFiltersInput["properties"];
  spacing: number;
  columns: Columns;
}) => {
  const { t } = useTranslate(["Global"]);
  const { getFormattedPrice } = useFormatting();

  if (isLoading) return <LoadingSpinner />;
  if (!templates.length)
    return (
      <Box
        sx={{
          padding: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {t("There seems to be nothing here", {
          ns: "Global",
        })}
      </Box>
    );

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        overflow: "auto",
        flex: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <Grid container spacing={spacing}>
          {templates.map((template, index) => {
            const attachments = template.attachments.map(processAttachment);

            const props = computeTemplateRelevantProperties({
              rootItemProps: template.rootItem.props2,
              templateTypeProps:
                template.implementsTemplateType?.templateType?.props2 ?? [],
            });

            const Info = hasTemplateTypeOrPropsInfo({
              templateTypeTitle:
                template.implementsTemplateType?.templateType.title,
              props,
            }) ? (
              <TemplateTypeAndPropsInfo
                templateTypeTitle={
                  template.implementsTemplateType?.templateType.title
                }
                props={props}
              />
            ) : null;

            return (
              <Grid key={template.id} size={columns}>
                <MediaCardItem
                  key={template.id}
                  selected={template.id === selectedTemplate?.id}
                  onClick={() => {
                    if (onTemplateClick) {
                      onTemplateClick(template);
                    } else {
                      setSelectedTemplate?.(
                        selectedTemplate?.id === template.id ? null : template
                      );
                    }
                  }}
                  title={template.title}
                  description={template.description}
                  price={getFormattedPrice(
                    template.proposedCalculation?.priceNetTotal || 0
                  )}
                  imageUrl={attachments.find(isImage)?.url}
                  images={attachments.filter(isImageOr3dModel)}
                  ActionButton={
                    <ButtonCircleWrapper>
                      <IconButton
                        onClick={event => {
                          event.stopPropagation();
                          setInspectedTemplate(template);
                        }}
                      >
                        <InfoOutlinedIcon color="secondary" />
                      </IconButton>
                    </ButtonCircleWrapper>
                  }
                  Info={Info}
                  Tags={
                    template.customTags.length > 0 ? (
                      <TagChips tags={template.customTags} wrap={true} />
                    ) : null
                  }
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <TablePagination
        component="div"
        count={totalCount}
        page={paginationModel.page}
        onPageChange={(event, newPage) => {
          setPaginationModel({
            pageSize: paginationModel.pageSize,
            page: newPage,
          });
        }}
        rowsPerPage={paginationModel.pageSize}
        onRowsPerPageChange={event => {
          setPaginationModel({
            pageSize: parseInt(event.target.value, 10),
            page: 0,
          });
        }}
        labelRowsPerPage={t("Items per page:", {
          ns: "Global",
        })}
      />
    </Stack>
  );
};
